import React, { useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  Node,
  Edge,
  MarkerType,
  NodeProps,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box } from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export type StepState = "TODO" | "DONE" | "CRITICAL";

export interface StepNodeData {
  label: string;
  templateUrl: string;
  state: StepState;
}

/* ------------------------------------------------------------------ */
/* State → Color map                                                   */
/* ------------------------------------------------------------------ */

const STATE_COLOR: Record<StepState, string> = {
  TODO: "#1976d2",      // 🟦 blue
  DONE: "#2e7d32",      // 🟩 green
  CRITICAL: "#d32f2f",  // 🟥 red
};

/* ------------------------------------------------------------------ */
/* Custom Node Component                                              */
/* ------------------------------------------------------------------ */

export const StepNode: React.FC<NodeProps<StepNodeData>> = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: 100,
        height: 80,
        backgroundColor: STATE_COLOR[data.state],
        color: "white",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        padding: 8,
        fontSize: "0.75rem",
        fontWeight: 600,
      }}
    >
      {/* incoming edge */}
      <Handle type="target" position={Position.Left} />

      {/* label */}
      <div>{data.label}</div>

      {/* template link (bottom-left) */}
      <a
        href={data.templateUrl}
        target="_blank"
        rel="noreferrer"
        title="Apri template"
        style={{
          position: "absolute",
          bottom: 4,
          left: 6,
          color: "#ffeb3b",
          display: "flex",
          alignItems: "center",
        }}
      >
        <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
      </a>

      {/* outgoing edge */}
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Node Types                                                         */
/* ------------------------------------------------------------------ */

const nodeTypes = {
  step: StepNode,
};

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */

export const FlowProgetti: React.FC = () => {
  /* compact steps definition */
  const steps = [
    ["1", "LEAD", "/docs/lead.pdf", "DONE"],
    ["2", "DEMO", "/docs/demo.pdf", "DONE"],
    ["3", "ANALISI REQUISITI", "/docs/analisi.pdf", "CRITICAL"],
    ["4", "KICK OFF", "/docs/kickoff.pdf", "TODO"],
    ["5", "BLUE PRINT", "/docs/blueprint.pdf", "TODO"],
    ["6", "COLLAUDO", "/docs/collaudo.pdf", "TODO"],
    ["7", "GO LIVE", "/docs/golive.pdf", "TODO"],
  ] as const;

  /* nodes */
  const [nodes] = useState<Node<StepNodeData>[]>(
    steps.map(([id, label, templateUrl, state], i) => ({
      id,
      type: "step",
      position: { x: i * 160, y: 0 },
      data: { label, templateUrl, state },
    }))
  );

  /* compact red directed edges */
  const redArrow = {
    type: "straight" as const,
    style: { stroke: "#d32f2f", strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#d32f2f" },
  };

  const edges: Edge[] = [
    { id: "e1-2", source: "1", target: "2", ...redArrow },
    { id: "e2-3", source: "2", target: "3", ...redArrow },
    { id: "e3-4", source: "3", target: "4", ...redArrow },
    { id: "e4-5", source: "4", target: "5", ...redArrow },
    { id: "e5-6", source: "5", target: "6", ...redArrow },
    { id: "e6-7", source: "6", target: "7", ...redArrow },
  ];

  return (
    <Box sx={{ mt: "60px", height: "500px", width: "100%" }}>
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView>
        <Background />
        <Controls />
      </ReactFlow>
    </Box>
  );
};
